/**
 * Main Floating Chat Hook
 * Combines state management and business logic for floating chat functionality
 * Replaces 10+ individual useState hooks with a structured state management system
 */

import { useReducer, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTranslatedToast } from '@/hooks/useTranslatedToast';
import { useCurrentTime, formatRelativeTime } from './useCurrentTime';
import {
  FloatingChatState,
  FloatingChatAction,
  initialFloatingChatState,
  floatingChatReducer
} from './useFloatingChatState';
import { useFloatingChatLogic } from './useFloatingChatLogic';
import { useOptimizedChat } from './useOptimizedChat';
import { loadChatState, clearChatState, filterValidChatStates, saveChatStateSafe } from '@/utils/chatStatePersistence';
import { visibilityManager } from '@/utils/visibilityManager';

export function useFloatingChat() {
  const toast = useTranslatedToast();
  const currentTime = useCurrentTime();
  const [state, dispatch] = useReducer(floatingChatReducer, initialFloatingChatState);
  
  // Callback to check if a chat window is visible
  const checkWindowVisible = useCallback((conversationId: string): boolean => {
    const chatState = state.activeChatStates.states[conversationId];
    return chatState && !chatState.isMinimized;
  }, [state.activeChatStates.states]);

  const {
    conversations,
    applicationGroups,
    messages,
    loading,
    totalUnreadCount,
    fetchMessages,
    sendMessage,
    markAsRead,
    handleTyping,
    subscribeToConversation,
    isUserOnline,
    getTypingUsers,
    userProfile,
    fetchConversations
  } = useOptimizedChat(checkWindowVisible);

  const {
    fetchUserRoles,
    fetchParticipantRoles,
    filteredConversations,
    filteredData,
    conversationCounts,
    openChatWindow,
    closeChatWindow,
    minimizeChatWindow,
    maximizeChatWindow,
    moveToFirstPosition,
    toggleRoleFilter,
    toggleTypeFilter,
    resetFilters,
    toggleGroupExpansion,
    updateOnlineStatus,
    isConversationOnline,
    isFirstPositionChat,
    getFilterButtonText,
    expandChatWindow,
    collapseChatWindow,
  } = useFloatingChatLogic({ 
    state, 
    dispatch, 
    conversations,
    applicationGroups,
    isUserOnline,
    fetchMessages,
    subscribeToConversation,
    markAsRead,
    userId: userProfile?.id
  });

  // Initialize data on mount - guard to prevent re-initialization
  const hasInitializedRef = useRef(false);
  
  useEffect(() => {
    if (!hasInitializedRef.current) {
      fetchUserRoles();
      hasInitializedRef.current = true;
    }
  }, [fetchUserRoles]);
  
  useEffect(() => {
    // Fetch conversations only once when userProfile becomes available
    if (userProfile?.id && !hasInitializedRef.current) {
      fetchConversations();
    }
  }, [userProfile?.id, fetchConversations]);

  // Fetch participant roles when conversations change
  useEffect(() => {
    if (conversations.length > 0) {
      fetchParticipantRoles();
    }
  }, [conversations.length, fetchParticipantRoles]);

  // Restore persisted chat state on mount - use ref to ensure it runs only once
  const hasRestoredRef = useRef(false);

  useEffect(() => {
    // Only restore once per session
    if (hasRestoredRef.current) return;
    
    // Wait for both user profile and conversations to be loaded
    if (!userProfile?.id || conversations.length === 0) return;
    
    const persistedStates = loadChatState(userProfile.id);
    
    if (!persistedStates || persistedStates.length === 0) {
      hasRestoredRef.current = true;
      return;
    }
    
    // Filter out conversations that no longer exist
    const validConversationIds = new Set(conversations.map(c => c.id));
    const validStates = filterValidChatStates(persistedStates, validConversationIds);

    if (validStates.length > 0) {
      console.log('[ChatPersistence] Restored', validStates.length, 'chat(s)');
      dispatch({ type: 'RESTORE_PERSISTED_STATE', payload: validStates });

      // Subscribe, fetch messages, and mark as read for restored conversations
      validStates.forEach(({ conversationId, isMinimized }) => {
        console.log('[ChatPersistence] Hydrating restored chat:', conversationId, { isMinimized });
        
        // Subscribe first (idempotent)
        subscribeToConversation(conversationId);
        
        // Always fetch the latest messages (cache-aware, won't duplicate fetches)
        fetchMessages(conversationId);
        
        // Mark read only if the window was restored expanded
        if (!isMinimized) {
          console.log('[ChatPersistence] Marking expanded chat as read:', conversationId);
          markAsRead(conversationId);
        }
      });
    }
    
    hasRestoredRef.current = true;
  }, [userProfile?.id, conversations, subscribeToConversation, fetchMessages, markAsRead]);

// Persist on page unload and visibility change (synchronous)
  // Uses centralized visibility manager for consistent debouncing
  useEffect(() => {
    if (!userProfile?.id) return;
    
    const persistImmediately = () => {
      const statesMap = new Map(
        Object.entries(state.activeChatStates.states).map(([id, s]) => [
          id,
          { isMinimized: s.isMinimized, isExpanded: s.isExpanded }
        ])
      );
      
      saveChatStateSafe(userProfile.id, statesMap, state.activeChatStates.order);
    };
    
    const handleBeforeUnload = () => {
      persistImmediately();
    };
    
    // Subscribe to visibility changes for persistence
    const unsubscribe = visibilityManager.subscribe((isVisible) => {
      if (!isVisible) {
        persistImmediately();
      }
    }, 'FloatingChat/Persist');
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      unsubscribe();
    };
  }, [userProfile?.id, state.activeChatStates]);

  // Cross-tab synchronization via storage event
  useEffect(() => {
    if (!userProfile?.id) return;
    
    const handleStorageChange = (event: StorageEvent) => {
      // Only respond to changes for this user's chat state
      if (!event.key || !event.key.startsWith('floating_chat_state:')) return;
      
      const expectedKey = `floating_chat_state:${userProfile.id}`;
      if (event.key !== expectedKey) return;
      
      console.log('[ChatPersistence] Storage change detected in another tab');
      
      // Avoid restore loop - only restore if we haven't recently restored
      if (hasRestoredRef.current && conversations.length > 0) {
        const persistedStates = loadChatState(userProfile.id);
        
        if (!persistedStates) {
          console.log('[ChatPersistence] Cross-tab: No data found');
          return;
        }
        
        // Check if order actually changed
        const currentOrder = state.activeChatStates.order;
        const newOrder = persistedStates.map(s => s.conversationId);
        
        const orderChanged = currentOrder.length !== newOrder.length || 
                           currentOrder.some((id, idx) => id !== newOrder[idx]);
        
        if (orderChanged) {
          console.log('[ChatPersistence] Cross-tab: Syncing state from other tab');
          
          const validConversationIds = new Set(conversations.map(c => c.id));
          const validStates = filterValidChatStates(persistedStates, validConversationIds);
          
          if (validStates.length > 0) {
            dispatch({ type: 'RESTORE_PERSISTED_STATE', payload: validStates });
            
            // Subscribe and fetch for cross-tab restored chats
            validStates.forEach(({ conversationId, isMinimized }) => {
              console.log('[CrossTab] Hydrating chat from other tab:', conversationId);
              subscribeToConversation(conversationId);
              fetchMessages(conversationId);
              if (!isMinimized) {
                markAsRead(conversationId);
              }
            });
          }
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [userProfile?.id, conversations, state.activeChatStates.order, subscribeToConversation, fetchMessages, markAsRead]);

  // Clear persisted state on logout
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' && userProfile?.id) {
        console.log('[ChatPersistence] Clearing chat state on logout');
        clearChatState(userProfile.id);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [userProfile?.id]);

  // Safety autoload: ensure visible chats have messages loaded
  useEffect(() => {
    state.activeChatStates.order.forEach((id) => {
      const chatState = state.activeChatStates.states[id];
      if (!chatState || chatState.isMinimized) return;
      
      // Check if messages are already loaded
      const conversationMessages = messages.get ? messages.get(id) : null;
      const hasMessages = conversationMessages && conversationMessages.length > 0;
      
      if (!hasMessages) {
        console.log('[ChatSafety] Autoload triggered for visible chat with no messages:', id);
        subscribeToConversation(id);
        fetchMessages(id);
      }
    });
  }, [state.activeChatStates.order, state.activeChatStates.states, messages, subscribeToConversation, fetchMessages]);

// Revalidate on focus/online for open chats (industry best practice)
  // Uses centralized visibility manager for consistent debouncing
  useEffect(() => {
    const refetchVisibleChats = () => {
      const visibleChats = state.activeChatStates.order.filter(id => {
        const chatState = state.activeChatStates.states[id];
        return chatState && !chatState.isMinimized;
      });
      
      if (visibleChats.length > 0) {
        console.log('[ChatRevalidate] Refetching', visibleChats.length, 'visible chat(s)');
        visibleChats.forEach(id => fetchMessages(id));
      }
    };
    
    // Subscribe to visibility changes for revalidation
    const unsubscribe = visibilityManager.subscribe((isVisible) => {
      if (isVisible) {
        console.log('[ChatRevalidate] Page became visible, revalidating chats');
        refetchVisibleChats();
      }
    }, 'FloatingChat/Revalidate');
    
    const handleOnline = () => {
      console.log('[ChatRevalidate] Network reconnected, revalidating chats');
      refetchVisibleChats();
    };
    
    window.addEventListener('online', handleOnline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      unsubscribe();
    };
  }, [state.activeChatStates.order, state.activeChatStates.states, fetchMessages]);

  // No longer need ephemeral cleanup

  // Handle global chat events
  useEffect(() => {
    const handleOpenApplicationChat = async (event: CustomEvent) => {
      const { applicationId, conversationId, conversationData, targetRoleType = 'talent', stageInfo } = event.detail;
      
      // If we have a conversation ID, try to open it directly
      if (conversationId) {
        let opened = false;
        const localConversation = conversations.find(c => c.id === conversationId);
        if (localConversation) {
          // Add stage info to local conversation if provided
          if (stageInfo) {
            const enrichedConversation = { ...localConversation, ...stageInfo };
            opened = openChatWindow(conversationId, enrichedConversation);
          } else {
            opened = openChatWindow(conversationId);
          }
        } else if (conversationData) {
          // Use conversation data from event if not found locally and add stage info
          const enrichedData = stageInfo ? { ...conversationData, ...stageInfo } : conversationData;
          opened = openChatWindow(conversationId, enrichedData);
        }

        // Fallback retry mechanism
        if (!opened) {
          setTimeout(() => {
            openChatWindow(conversationId);
          }, 500);
        }
        return;
      }

      // Create role-specific application conversation
      if (applicationId) {
        // First, try to find existing conversation locally
        const candidateConvs = conversations.filter(c =>
          c.job_application_id === applicationId &&
          (c.conversation_type === 'application' ||
           c.conversation_category === 'application' ||
           c.conversation_category === 'hiring')
        );

        let existingConv = candidateConvs.find(c => (c as any).primary_role_context === targetRoleType);
        if (!existingConv && candidateConvs.length > 0) {
          existingConv = candidateConvs.sort((a, b) =>
            new Date(b.last_message_at || 0).getTime() - new Date(a.last_message_at || 0).getTime()
          )[0];
        }

        if (existingConv) {
          console.log('[CHAT] Opening existing application chat', { 
            applicationId, 
            conversationId: existingConv.id, 
            targetRoleType 
          });
          const enriched = stageInfo ? { ...existingConv, ...stageInfo } : existingConv;
          const opened = openChatWindow(existingConv.id, enriched);
          if (!opened) {
            setTimeout(() => openChatWindow(existingConv.id, enriched), 200);
          }
          return;
        }

        // Fallback: create via RPC if no existing conversation found
        try {
          console.log('[CHAT] Creating new application conversation via RPC', { applicationId, targetRoleType });
          const { data: conversationResult, error } = await supabase.rpc(
            'create_application_conversation_with_role',
            { 
              application_uuid: applicationId,
              target_role_type: targetRoleType
            }
          );

          if (error) {
            console.error('Failed to create application conversation:', error);
            toast.raw.error('Failed to open chat. Please try again.');
            return;
          }

          const newConversationId = conversationResult as string;
          if (newConversationId) {
            // Dispatch immediate feedback event
            window.dispatchEvent(new CustomEvent('chat-opening', { 
              detail: { conversationId: newConversationId } 
            }));
            
            // Wait for conversations list to refresh
            await fetchConversations({ silent: true });
            
            // Now attempt to open the chat window
            const opened = openChatWindow(newConversationId);
            
            // If still not found, retry after a short delay
            if (!opened) {
              setTimeout(() => openChatWindow(newConversationId), 300);
            }
            
            // Dispatch success event
            window.dispatchEvent(new CustomEvent('chat-window-opened', {
              detail: { conversationId: newConversationId }
            }));
          }
        } catch (error) {
          console.error('Error creating application conversation:', error);
          toast.raw.error('Unable to open chat. Please check your connection.');
        }
      }
    };

    const handleOpenChatWindow = (event: CustomEvent) => {
      const { conversationId } = event.detail;
      
      // Open directly by ID - the conversation should already be in the conversations list after refresh
      const localConversation = conversations.find(c => c.id === conversationId);
      if (localConversation) {
        openChatWindow(conversationId);
      } else {
        // If not found locally, wait and retry
        setTimeout(() => {
          const updatedConversation = conversations.find(c => c.id === conversationId);
          if (updatedConversation) {
            openChatWindow(conversationId);
          }
        }, 500);
      }
    };

    const handleOpenRecruiterChat = async (event: CustomEvent) => {
      console.debug('[CHAT] Handling openRecruiterChat event:', event.detail);
      try {
        const { jobId, companyId, jobTitle, companyName } = event.detail;
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || !jobId || !companyId) return;

        // First, try to find existing conversation locally
        const candidateConvs = conversations.filter(c =>
          c.job_id === jobId &&
          c.company_id === companyId &&
          (c.conversation_category === 'hiring' || c.conversation_type === 'direct')
        );

        if (candidateConvs.length > 0) {
          // Pick the most recent one
          const existingConv = candidateConvs.sort((a, b) =>
            new Date(b.last_message_at || 0).getTime() - new Date(a.last_message_at || 0).getTime()
          )[0];

          console.log('[CHAT] Opening existing recruiter chat', { 
            jobId, 
            companyId,
            conversationId: existingConv.id 
          });
          
          const opened = openChatWindow(existingConv.id);
          if (!opened) {
            setTimeout(() => openChatWindow(existingConv.id), 200);
          }
          return;
        }

        // Fallback: create via RPC if no existing conversation found
        console.log('[CHAT] Creating new recruiter conversation via RPC', { jobId, companyId });
        const { data: convId, error } = await supabase.rpc(
          'create_or_get_recruiter_company_conversation',
          {
            recruiter_user_id_param: user.id,
            job_id_param: jobId,
            company_id_param: companyId,
          }
        );

        if (error) {
          console.error('[CHAT] RPC create_or_get_recruiter_company_conversation failed:', error);
          return;
        }

        const conversationId = convId as string;

        // Refresh conversations so the chat list includes this conversation
        window.dispatchEvent(new CustomEvent('refresh-conversations'));

        // Provide minimal data for immediate UI while list refreshes
        const conversationData = {
          id: conversationId,
          title: `${jobTitle || 'Recruiter Chat'}${companyName ? ' - ' + companyName : ''}`,
          job_id: jobId,
          company_id: companyId,
          company_name: companyName,
          job_title: jobTitle,
          conversation_type: 'direct',
          conversation_category: 'hiring',
          last_message_at: new Date().toISOString(),
          unread_count: 0,
        } as any;

        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('open-chat-window', {
            detail: { conversationId, conversationData }
          }));
        }, 300);
      } catch (error) {
        console.error('[CHAT] Error handling openRecruiterChat:', error);
      }
    };

    // Handle conversations refresh
    const handleRefreshConversations = () => {
      fetchConversations();
    };

    // Handle mobile chat toggle
    const handleToggleMobileChat = () => {
      dispatch({ type: 'SET_EXPANDED', payload: !state.isExpanded });
    };

    window.addEventListener('openApplicationChat', handleOpenApplicationChat as EventListener);
    window.addEventListener('openRecruiterChat', handleOpenRecruiterChat as EventListener);
    window.addEventListener('open-chat-window', handleOpenChatWindow as EventListener);
    window.addEventListener('refresh-conversations', handleRefreshConversations as EventListener);
    window.addEventListener('toggle-mobile-chat', handleToggleMobileChat as EventListener);
    
    return () => {
      window.removeEventListener('openApplicationChat', handleOpenApplicationChat as EventListener);
      window.removeEventListener('openRecruiterChat', handleOpenRecruiterChat as EventListener);
      window.removeEventListener('open-chat-window', handleOpenChatWindow as EventListener);
      window.removeEventListener('refresh-conversations', handleRefreshConversations as EventListener);
      window.removeEventListener('toggle-mobile-chat', handleToggleMobileChat as EventListener);
    };
  }, [conversations, openChatWindow, fetchConversations, state.isExpanded]);

  // Action creators for dispatching state changes
  const actions = {
    setExpanded: useCallback((expanded: boolean) => {
      dispatch({ type: 'SET_EXPANDED', payload: expanded });
    }, [dispatch]),

    updateChatState: useCallback((conversationId: string, updates: Partial<any>) => {
      dispatch({ 
        type: 'UPDATE_CHAT_STATE', 
        payload: { conversationId, updates } 
      });
    }, [dispatch]),

    clearError: useCallback(() => {
      dispatch({ type: 'SET_ERROR', payload: null });
    }, [dispatch]),
  };

  // Helper functions with auto-updating timestamps
  const formatTime = useCallback((timestamp: string) => {
    return formatRelativeTime(timestamp, currentTime);
  }, [currentTime]);

  const getInitials = useCallback((name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }, []);

  return {
    // State
    ...state,
    
    // From useOptimizedChat
    conversations,
    applicationGroups,
    messages,
    loading: loading || state.isLoading,
    totalUnreadCount,
    userProfile,
    
    // Computed state
    filteredConversations,
    filteredData,
    conversationCounts,
    
    // Actions
    ...actions,
    
    // Chat window management
    openChatWindow,
    closeChatWindow,
    minimizeChatWindow,
    maximizeChatWindow,
    expandChatWindow,
    collapseChatWindow,
    moveToFirstPosition,
    
    // Filter management
    toggleRoleFilter,
    toggleTypeFilter,
    resetFilters,
    getFilterButtonText,
    
    // Group management
    expandedGroups: state.expandedGroups,
    toggleGroupExpansion,
    
    // Role data
    userRoles: state.userRoles,
    conversationUserRoles: state.participantRoles,
    
    // Remove ephemeral conversations as we're using real conversations
    // ephemeralConversations: state.ephemeralConversations,
    
    // Chat operations (from useOptimizedChat)
    fetchMessages,
    sendMessage,
    markAsRead,
    handleTyping,
    subscribeToConversation,
    getTypingUsers,
    
    // Helpers
    isConversationOnline,
    isFirstPositionChat,
    updateOnlineStatus,
    formatTime,
    getInitials,
    
    // Utility
    hasActiveChats: state.activeChatStates.order.length > 0,
    hasFilters: !state.activeRoleFilters.has('all') || !state.activeTypeFilters.has('all'),
  };
}